import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, map, switchMap, of } from 'rxjs';
import { environment } from '@environments/environment';
import { ChatRequest, ChatAgentRequest, ChatAgentResponse, ChatConnectionsResponse } from '@models/master-search.models';
import { LoginService } from './login.service';

@Injectable({
  providedIn: 'root'
})
export class MasterSearchService {
  constructor(
    private http: HttpClient,
    private login: LoginService
  ) {}

  private router = inject(Router);

  private getHeaders(): Observable<{ [key: string]: string }> {
    return this.login.loginUserEmail.pipe(
      map(email => {
        if (!email) {
          console.warn('User email not available for headers');
          // Return an empty object or handle as per API requirements if email is not available
          return {} as { [key: string]: string }; 
        }
        return {
          userId: email,
        };
      })
    );
  }

  masterSearchGeneric<T>(
    request: Partial<ChatRequest | ChatAgentRequest>,
    type: string = 'data'
  ): Observable<ChatAgentResponse<T>> {
    const URL = `${environment.baseUrlMasterSearch}/master/v1/master-search`;
    const params = new HttpParams().set('type', type);

    return this.getHeaders().pipe(
      switchMap(headers => {
        return this.http.post<ChatAgentResponse<T>>(URL, request, {
          params,
          headers: headers, // Use the headers obtained from the observable
        });
      })
    );
  }

  masterSearch<T>(
    type: string,
    value: string,
    template: string,
    name?: string
  ): Observable<ChatAgentResponse<T>> {
    const chatRequest: ChatRequest = {
      inputField: {
        name: name || undefined,
        value: value,
      }
    };
    if (template !== '') {
      chatRequest.templates = [template];
    }
    return this.masterSearchGeneric<T>(chatRequest, type);
  }

  masterSearchConnections(
    value: string,
    template: string,
    name?: string
  ): Observable<ChatAgentResponse<ChatConnectionsResponse>> {
    return this.masterSearch<ChatConnectionsResponse>(
      'connections',
      value,
      template,
      name
    );
  }

  masterSearchData<T>(
    value: string,
    template: string,
    name?: string
  ): Observable<ChatAgentResponse<T>> {
    return this.masterSearch<T>('data', value, template, name);
  }

  masterSearchByTemplate(template: string, value: string, name?: string) {
    return this.masterSearchData<any>(value, template, name).pipe(
      map((response) => response.data[template])
    );
  }
}
